import { useCallback, useEffect, useState } from "react";
import {
  agent as agentApi,
  kg,
  messaging,
  type Conversation,
  type DuplicateSuggestion,
  type Person,
  type PersonDetail,
} from "../api";
import { NetIcon, netInfo } from "../cockpit-ui";
import { Button, Field, List, ListRow, Spinner } from "./ui";

const REASON_LABEL: Record<DuplicateSuggestion["reason"], string> = {
  email: "même e-mail",
  phone: "même téléphone",
  name: "même nom",
};

// Bloc « fusion de contacts » réutilisable (#10, session merge-contact).
// À partir de la personne courante : ses identités réseau (avec démerge), les
// doublons probables, et une recherche pour fusionner avec un autre contact.
// La fusion garde TOUJOURS la personne courante (cible).
//
// Deux modes selon le contexte :
// - AVEC `conversationId` (panneau droit messagerie) : la recherche porte sur
//   les CONVERSATIONS directes (qui portent les vrais noms du carnet — un
//   `kg_person` n'a souvent qu'un numéro) ; on résout la personne de la conv.
// - SANS `conversationId` (fiche CRM) : la recherche porte directement sur les
//   PERSONNES (la fiche les nomme déjà), `kg.merge` reçoit leur id.
export function MergeContact({
  personId,
  conversationId,
  onChanged,
}: {
  personId: string;
  /** Conversation courante (mode messagerie) — exclue des résultats. Absent = mode fiche CRM (recherche par personnes). */
  conversationId?: string;
  /** Appelé après une fusion/démerge — laisse le parent rafraîchir le dossier. */
  onChanged?: () => void;
}) {
  const byConversation = conversationId != null;
  const [detail, setDetail] = useState<PersonDetail | null>(null);
  const [dups, setDups] = useState<DuplicateSuggestion[]>([]);
  // Source de recherche chargée une fois ; les résultats sont DÉRIVÉS au rendu.
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    kg.person(personId)
      .then(setDetail)
      .catch(() => setDetail(null));
    kg.suggestions(personId)
      .then((r) => setDups(r.suggestions))
      .catch(() => setDups([]));
    if (byConversation) {
      messaging
        .conversations()
        .then((r) => setConvs(r.conversations))
        .catch(() => setConvs([]));
    } else {
      kg.persons()
        .then((r) => setPersons(r.persons))
        .catch(() => setPersons([]));
    }
  }, [personId, byConversation]);

  // Le parent remonte ce bloc via key={personId} → l'état part déjà à neuf ;
  // l'effet ne fait qu'amorcer le chargement (setState seulement en .then).
  useEffect(() => {
    reload();
  }, [reload]);

  // Recherches dérivées (pures). Mode messagerie : conversations directes nommées,
  // hors la courante. Mode fiche : personnes par nom, hors la personne courante.
  const q = query.trim().toLowerCase();
  const convResults =
    q && byConversation
      ? convs
          .filter((c) => c.kind === "dm" && c.id !== conversationId)
          .filter((c) => (c.title || "").toLowerCase().includes(q))
          .slice(0, 8)
      : [];
  const personResults =
    q && !byConversation
      ? persons
          .filter((p) => p.id !== personId)
          .filter((p) => (p.displayName || "").toLowerCase().includes(q))
          .slice(0, 8)
      : [];
  const noResults = q && convResults.length === 0 && personResults.length === 0 && !busy;

  // Fusionne le contact d'une autre conversation : résout sa personne puis
  // l'absorbe dans la personne courante (cible).
  async function mergeConversation(otherConversationId: string) {
    setBusy(true);
    setError(null);
    try {
      const { dossier } = await agentApi.dossier(otherConversationId);
      const sourceId = dossier?.personId;
      if (!sourceId) {
        setError("Contact non résolu pour cette conversation.");
        return;
      }
      if (sourceId === personId) {
        setError("C'est déjà la même personne.");
        return;
      }
      await kg.merge(personId, sourceId);
      setConfirmMerge(null);
      setQuery("");
      reload();
      onChanged?.();
    } catch {
      setError("La fusion a échoué.");
    } finally {
      setBusy(false);
    }
  }

  // Fusionne un doublon déjà résolu (sourceId = personId du candidat).
  async function mergePerson(sourceId: string) {
    setBusy(true);
    setError(null);
    try {
      await kg.merge(personId, sourceId);
      setConfirmMerge(null);
      reload();
      onChanged?.();
    } catch {
      setError("La fusion a échoué.");
    } finally {
      setBusy(false);
    }
  }

  async function split(identityId: string) {
    setBusy(true);
    try {
      await kg.split(personId, identityId);
      reload();
      onChanged?.();
    } finally {
      setBusy(false);
    }
  }

  // Dédup d'affichage : deux lignes strictement identiques (même réseau + même
  // remoteId/handle) ne servent à rien. On garde l'ordre d'origine.
  const identities = (() => {
    const seen = new Set<string>();
    return (detail?.identities ?? []).filter((id) => {
      const key = `${id.network}|${id.remoteId}|${id.handle}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();
  const canSplit = identities.length > 1;

  return (
    <>
      {identities.length > 0 && (
        <List header="Identités réseau">
          {identities.map((id) => {
            const n = netInfo(id.network);
            return (
              <ListRow
                key={id.id}
                leading={<NetIcon network={id.network} size={14} />}
                title={
                  <span className="identity-cell">
                    <span>{id.displayName || id.handle || id.remoteId}</span>
                    {/* Sous-titre = handle/numéro : distingue deux identités de même
                        nom sur le même réseau (ex. 2 WhatsApp « Alice Van Aker »). */}
                    {id.handle && id.handle !== id.displayName && (
                      <span className="identity-handle">{id.handle}</span>
                    )}
                  </span>
                }
                value={<span className="muted">{n.label}</span>}
                trailing={
                  canSplit ? (
                    <Button
                      variant="ghost"
                      small
                      disabled={busy}
                      onClick={() => split(id.id)}
                      aria-label={`Détacher ${id.displayName || id.handle} de ce contact`}
                    >
                      Détacher
                    </Button>
                  ) : undefined
                }
              />
            );
          })}
        </List>
      )}

      {dups.length > 0 && (
        <List header="Doublons probables">
          {dups.map((d) => (
            <ListRow
              key={d.id}
              title={d.displayName || "(sans nom)"}
              value={<span className="muted">{REASON_LABEL[d.reason]}</span>}
              trailing={
                confirmMerge === d.id ? (
                  <Button variant="danger" small disabled={busy} onClick={() => mergePerson(d.id)}>
                    Confirmer
                  </Button>
                ) : (
                  <Button variant="secondary" small onClick={() => setConfirmMerge(d.id)}>
                    Fusionner
                  </Button>
                )
              }
            />
          ))}
        </List>
      )}

      <List header="Fusionner un autre contact">
        <div style={{ padding: "8px 12px" }}>
          <Field
            label={byConversation ? "Rechercher une conversation" : "Rechercher un contact"}
            placeholder="Nom du contact…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {busy && (
          <div style={{ padding: "8px 12px" }}>
            <Spinner />
          </div>
        )}
        {error && (
          <p className="info-empty" style={{ padding: "0 12px 8px", color: "var(--danger)" }}>
            {error}
          </p>
        )}
        {convResults.map((c) => {
          const n = netInfo(c.network);
          return (
            <ListRow
              key={c.id}
              leading={<NetIcon network={c.network} size={14} />}
              title={c.title || "(sans nom)"}
              value={<span className="muted">{n.label}</span>}
              trailing={
                confirmMerge === c.id ? (
                  <Button
                    variant="danger"
                    small
                    disabled={busy}
                    onClick={() => mergeConversation(c.id)}
                  >
                    Confirmer
                  </Button>
                ) : (
                  <Button variant="secondary" small onClick={() => setConfirmMerge(c.id)}>
                    Fusionner
                  </Button>
                )
              }
            />
          );
        })}
        {personResults.map((p) => (
          <ListRow
            key={p.id}
            title={p.displayName || "(sans nom)"}
            value={p.primaryEmail ? <span className="muted">{p.primaryEmail}</span> : undefined}
            trailing={
              confirmMerge === p.id ? (
                <Button variant="danger" small disabled={busy} onClick={() => mergePerson(p.id)}>
                  Confirmer
                </Button>
              ) : (
                <Button variant="secondary" small onClick={() => setConfirmMerge(p.id)}>
                  Fusionner
                </Button>
              )
            }
          />
        ))}
        {noResults && (
          <p className="info-empty" style={{ padding: "0 12px 8px" }}>
            {byConversation ? "Aucune conversation trouvée." : "Aucun contact trouvé."}
          </p>
        )}
      </List>
    </>
  );
}
